import React, { ChangeEventHandler, ReactNode } from "react";
import {
  Grid,
  ListSubheader,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import { CloudDownload } from "iconoir-react";
import { Option } from "../model";
import {
  AmlStatus,
  OnboardingModel,
  StatusNaming,
} from "../constant/amlConstant";
import {
  IDVGroupMarkers,
  PEPGroupMarkers,
  RiskRatingGroupMarkers,
  SanctionsGroupMarkers,
} from "../constant/markerConstant";
import ExportDialog from "./ExportDialog";
import useBoolean from "../hooks/useBoolean";
import { SubCustomer } from "../api/transformers/aml";

export const SEARCH_STATUS_KEYS = [
  "status",
  "pepStatus",
  "sancStatus",
  "riskRatingStatus", // was "riskStatus"
  "idvStatus",
] as const;

export type FilterValue = {
  accountId?: string;
  personId?: string;
  onboardingModel?: string;
  customerId?: string;
} & {
  [k in (typeof SEARCH_STATUS_KEYS)[number]]?: string;
};

interface FilterProps {
  value: FilterValue;
  showCustomerFilter: boolean;
  subCustomers: SubCustomer[];
  loading?: boolean;
  onChange: (f: FilterValue, triggerFetch?: boolean) => void;
  onSearch: () => void;
  onReset: () => void;
}

const Filter: React.FC<FilterProps> = ({
  value,
  showCustomerFilter,
  subCustomers,
  loading,
  onChange,
  onSearch,
  onReset,
}) => {
  const [
    exportDialogOpen,
    { setTrue: openExportDialog, setFalse: closeExportDialog },
  ] = useBoolean(false);
  const statusObject = SEARCH_STATUS_KEYS.reduce((acc, key) => {
    if (value[key]) {
      acc[key] = value[key];
    }
    return acc;
  }, {} as Pick<FilterValue, (typeof SEARCH_STATUS_KEYS)[number]>);
  const statusValue = JSON.stringify(statusObject);
  const handleAccountIdChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const accountId = e.target.value;
    onChange({
      ...value,
      accountId,
    });
  };
  const handleInvestorIdChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const personId = e.target.value;
    onChange({
      ...value,
      personId,
    });
  };
  const handleOnboardingModelChange: ChangeEventHandler<HTMLInputElement> = (
    e: SelectChangeEvent<string>
  ) => {
    const onboardingModel = e.target.value === "/" ? undefined : e.target.value;
    onChange(
      {
        ...value,
        onboardingModel,
      },
      true
    );
  };
  const handleCustomerChange = (v: string) => {
    const customerId = v;
    onChange(
      {
        ...value,
        customerId,
      },
      true
    );
  };
  const handleStatusChange: ChangeEventHandler<HTMLInputElement> = (
    e: SelectChangeEvent<string>
  ) => {
    const jsonString = e.target.value;
    const obj = JSON.parse(jsonString);
    onChange(
      {
        ...value,
        ...SEARCH_STATUS_KEYS.reduce((acc, key) => {
          acc[key] = undefined;
          return acc;
        }, {} as { [k in (typeof SEARCH_STATUS_KEYS)[number]]: undefined }),
        ...obj,
      },
      true
    );
  };
  const onboardingModelOptions = getOnboardingModelOptions();
  const statusOptions = getStatusOptions();
  const customerOptions = getCustomerOptions(subCustomers);
  const renderStatusOptions = (options: (SingleOption | OptionGroup)[]) => {
    return options.map((el, i): ReactNode => {
      if ("options" in el) {
        const group = el as OptionGroup;
        return [
          <ListSubheader key={`group-${group.label}-${i}`}>
            {group.label}
          </ListSubheader>,
          ...renderStatusOptions(
            group.options.map((o, idx) => ({
              ...o,
              // ensure uniqueness when recursing by tweaking label/value if needed
              label: o.label,
              value: o.value,
              fullName: o.fullName,
            })) as SingleOption[]
          ),
        ];
      } else {
        const opt = el as SingleOption;
        return (
          <MenuItem key={`status-${opt.value}-${i}`} value={opt.value}>
            {opt.label}
          </MenuItem>
        );
      }
    });
  };
  return (
    <Grid container spacing={3} alignItems="center" sx={{ width: "100%" }}>
      {showCustomerFilter && (
        <Grid item xs={12} sm={6} md={2}>
          <Autocomplete
            size="small"
            disabled={loading}
            getOptionLabel={(option) => option.label || ""}
            options={customerOptions}
            sx={{ width: 1, marginRight: 2 }}
            value={
              value.customerId
                ? customerOptions.find(
                  (option) => option.value === value.customerId
                )
                : null
            }
            onChange={(_, selectedOption) => {
              onChange(
                {
                  ...value,
                  customerId: selectedOption ? selectedOption.value : undefined,
                },
                true
              );
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <div>
                  {option.label} (
                  <span style={{ fontWeight: 600, fontStyle: "italic" }}>
                    {option.value}
                  </span>
            )
                </div>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Filter by Firm" />
            )}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          label="Filter by account ID"
          size="small"
          value={value.accountId || ""}
          disabled={loading}
          onChange={(e) => {
            const v = e.target.value;
            onChange({ ...value, accountId: v === "" ? undefined : v });
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          label="Filter by investor ID"
          size="small"
          value={value.personId || ""}
          disabled={loading}
          onChange={(e) => {
            const v = e.target.value;
            onChange({ ...value, personId: v === "" ? undefined : v });
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          select
          size="small"
          fullWidth
          value={statusValue}
          disabled={loading}
          label="Filter by Status"
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  p: 0,
                },
              },
            },
          }}
          onChange={handleStatusChange}
        >
          {renderStatusOptions(statusOptions)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          select
          size="small"
          fullWidth
          value={value.onboardingModel || "/"}
          disabled={loading}
          label="Onboarding Model"
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  p: 0,
                },
              },
            },
          }}
          onChange={handleOnboardingModelChange}
        >
          {onboardingModelOptions.map((option) => {
            return (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            );
          })}
        </TextField>
      </Grid>
      <Grid item sx={{ flexGrow: 1 }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ width: "100%" }}
        >
          <Button color="primary" disabled={loading} onClick={onSearch}>
        Search
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<CloudDownload />}
            disabled={loading}
            onClick={openExportDialog}
          >
        Export
          </Button>
          <Button disabled={loading} variant="outlined" onClick={onReset}>
            <ReplayIcon />
          </Button>
        </Stack>
      </Grid>
      <ExportDialog
        open={exportDialogOpen}
        onClose={closeExportDialog}
        filter={value}
      />
    </Grid>
  );
};

function getOnboardingModelOptions(): Option[] {
  return [
    { label: "All", value: "/" },
    { label: "Non-Reliance", value: OnboardingModel.NONE_RELIANCE },
    { label: "Reliance", value: OnboardingModel.RELIANCE },
  ];
}

function getCustomerOptions(subCustomers: SubCustomer[]): Option[] {
  return subCustomers.map((customer) => {
    return {
      value: customer.customerId,
      label: customer.name
    };
  });
}

interface SingleOption {
  label: string;
  value: string;
  fullName: string;
}

interface OptionGroup {
  label: string;
  options: SingleOption[];
}
function getStatusOptions(): (SingleOption | OptionGroup)[] {
  const statues = [
    AmlStatus.SUB,
    AmlStatus.REF,
    AmlStatus.EVI,
    AmlStatus.AUT,
    AmlStatus.NRQ,
    AmlStatus.ACT,
    AmlStatus.REJ,
  ] as const;
  return [
    {
      label: "Any",
      value: "{}",
      fullName: "Any",
    },
    {
      label: "AML Status",
      options: statues.map((status) => ({
        label: StatusNaming[status],
        value: JSON.stringify({ status }),
        fullName: StatusNaming[status],
      })),
    },
    {
      label: "ID&V",
      options: [
        {
          label: "Referred",
          value: JSON.stringify({ idvStatus: IDVGroupMarkers.REF }),
          fullName: "Referred ID&V",
        },
      ],
    },
    {
      label: "Sanctions",
      options: [
        {
          label: "Referred",
          value: JSON.stringify({ sancStatus: SanctionsGroupMarkers.REF }),
          fullName: "Referred Sanctions",
        },
      ],
    },
    {
      label: "PEP",
      options: [
        {
          label: "Referred",
          value: JSON.stringify({ pepStatus: PEPGroupMarkers.REF }),
          fullName: "Referred PEP",
        },
      ],
    },
    {
      label: "Risk Rating",
      options: [
        {
          label: "Referred",
          // was { riskStatus: RiskRatingGroupMarkers.REF }
          value: JSON.stringify({ riskRatingStatus: RiskRatingGroupMarkers.REF }),
          fullName: "Referred Risk Rating",
        },
      ],
    },
  ];
}

export default Filter;
